import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash, MagnifyingGlass, FileText, ArrowSquareOut, Upload, ArrowsLeftRight, CheckSquare, Square, Warning, Briefcase } from '@phosphor-icons/react';
import { ViewToggle, useViewMode } from '../components/ViewToggle';
import { PowerTable } from '../components/PowerTable';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenantPath } from '../lib/tenantPath';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import BulkUploadModal from '../components/BulkUploadModal';
import useAuthStore from '../store/authStore';
import { useGmTransport, pickGmPct } from '../lib/gmTransport';
import GmTransportToggle from '../components/GmTransportToggle';
import { confirm } from '../components/ConfirmDialog';
import useBulkSelection from '../hooks/useBulkSelection';
import BulkDeleteDialog, { BulkActionBar } from '../components/BulkActions';
import SummaryCards, { inr } from '../components/SummaryCards';
import OwnerFilter from '../components/OwnerFilter';
import PipelineToolbar from '../components/PipelineToolbar';
import { computeCloseWindow, withinCloseWindow, sortRows, isForecastOverdue } from '../lib/pipelineFilters';
import { CurrencyInr, FileText as FileTextIcon, CheckCircle, ChartLine } from '@phosphor-icons/react';

import { notify } from '../lib/notify';
import { PageSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Indian states organized by region
const STATES_BY_REGION = {
  'South India': ['Andhra Pradesh', 'Karnataka', 'Kerala', 'Tamil Nadu', 'Telangana', 'Puducherry', 'Lakshadweep', 'Andaman and Nicobar Islands'],
  'North India': ['Delhi', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Ladakh', 'Punjab', 'Rajasthan', 'Uttar Pradesh', 'Uttarakhand', 'Chandigarh'],
  'East India': ['Bihar', 'Jharkhand', 'Odisha', 'West Bengal'],
  'West India': ['Goa', 'Gujarat', 'Maharashtra', 'Dadra and Nagar Haveli and Daman and Diu'],
  'Central India': ['Chhattisgarh', 'Madhya Pradesh'],
  'Northeast India': ['Arunachal Pradesh', 'Assam', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Sikkim', 'Tripura']
};

const ALL_STATES = Object.entries(STATES_BY_REGION).flatMap(([region, states]) => 
  states.map(state => ({ state, region }))
);

const CUSTOMER_TYPES = [
  'Dealer',
  'Standalone Hospital',
  'Corporate Hospital',
  'Government Body',
  'College'
];

const Leads = () => {
  const { user } = useAuthStore();
  const canBulkUpload = ['Vice President', 'President'].includes(user?.role);
  const canBulkReassign = ['National Sales Manager', 'Vice President', 'President'].includes(user?.role);
  const canBulkDelete = ['Vice President', 'President'].includes(user?.role);
  const canBulkSelect = canBulkReassign || canBulkDelete;
  // iter92 — Tender Executive can VIEW leads (esp. leads auto-created from
  // their tender awards) but cannot add leads or quotes outside the tender
  // flow. Everyone else keeps the existing behaviour.
  const canAddLead = user?.role !== 'Tender Executive';
  const canCreateQuote = user?.role !== 'Tender Executive';
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [leads, setLeads] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState([]); // Selected owner user_ids for manager filter
  // Summary rollup — always mirrors the current visibility scope, so a Sales
  // Exec sees only their own numbers, a President sees the whole tenant.
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  // "Promised closing this <period>" — set of lead_ids the current user has
  // committed to in an OPEN review period. Sticky-pinned to top + tagged.
  const [promisedMap, setPromisedMap] = useState({}); // { lead_id: label }
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useViewMode('leads');
  const [closeWindow, setCloseWindow] = useState('all');           // pipeline toolbar
  const [sortBy, setSortBy] = useState('created_desc');
  const [forecastOverdueOnly, setForecastOverdueOnly] = useState(false);
  // Status filter — either driven by a chip below the KPI strip OR by
  // clicking one of the KPI cards. Values map to `lead.status` groupings:
  //   'all'      → no filter
  //   'open'     → NOT (won|completed|lost) — the live pipeline
  //   'quoted'   → status IN (quoted|quote_approved|negotiation)
  //   'approved' → status = quote_approved  (approved but not yet won)
  //   'won'      → status IN (won|completed)
  //   'lost'     → status = lost
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerSource, setCustomerSource] = useState('existing'); // 'existing' or 'new'
  const navigate = useNavigate();
  const tp = useTenantPath();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    lead_value: '',
    products: [],
    customer_type: 'Dealer',
    expected_closing_date: '',
    state: '',
    city: '',
    region: '',
    customer_id: '',
    is_greenfield: false,
  });

  useEffect(() => {
    fetchLeads();
    fetchProducts();
    fetchCustomers();
    fetchSummary();
    fetchPromised();

    // Handle redirect back from customer creation
    if (location.state?.openAddLead && location.state?.preselectedCustomer) {
      const cust = location.state.preselectedCustomer;
      setFormData(prev => ({
        ...prev,
        customer_id: cust.id,
        name: cust.poc_name || '',
        company: cust.organisation_name || '',
        email: cust.email || '',
        phone: cust.phone || '',
        address: cust.address || '',
        city: cust.city || '',
        state: cust.state || '',
        customer_type: cust.industry_type || 'Dealer',
        region: ''
      }));
      // Auto-fill region from state
      const stateData = ALL_STATES.find(s => s.state === cust.state);
      if (stateData) {
        setFormData(prev => ({ ...prev, region: stateData.region }));
      }
      setCustomerSource('existing');
      setShowModal(true);
      // Clear navigation state
      window.history.replaceState({}, document.title);
    }
  }, []);

  // Refetch leads whenever the owner filter changes.
  useEffect(() => {
    fetchLeads();
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerFilter]);

  const fetchLeads = async () => {
    try {
      const params = ownerFilter.length ? `?owner_ids=${ownerFilter.join(',')}` : '';
      const response = await axios.get(`${API}/leads${params}`);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch the visibility-scoped summary rollup for the top cards.
  // Errors are swallowed silently — the cards will just show blank rather
  // than break the whole page for a nice-to-have widget.
  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const params = ownerFilter.length ? `?owner_ids=${ownerFilter.join(',')}` : '';
      const response = await axios.get(`${API}/leads/summary${params}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching leads summary:', error);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  // Fetch the current user's "Promised closing this ___" lead ids (from any
  // open review period they've committed in). Powers the sticky-top ordering
  // + tag/badge on the row. Silent on failure — reviews are an optional layer.
  const fetchPromised = async () => {
    try {
      const r = await axios.get(`${API}/reviews/promised-lead-ids`);
      setPromisedMap(r.data.period_labels || {});
    } catch (e) { /* silent — feature is optional */ }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleCustomerSelect = (customerId) => {
    if (!customerId) {
      setFormData(prev => ({ ...prev, customer_id: '', name: '', company: '', email: '', phone: '', address: '', city: '', state: '', region: '' }));
      return;
    }
    const cust = customers.find(c => c.id === customerId);
    if (cust) {
      const stateData = ALL_STATES.find(s => s.state === cust.state);
      setFormData(prev => ({
        ...prev,
        customer_id: cust.id,
        name: cust.poc_name || prev.name,
        company: cust.organisation_name || prev.company,
        email: cust.email || prev.email,
        phone: cust.phone || prev.phone,
        address: cust.address || prev.address,
        city: cust.city || prev.city,
        state: cust.state || prev.state,
        region: stateData ? stateData.region : prev.region,
        customer_type: cust.industry_type || prev.customer_type,
      }));
    }
  };

  const handleNewCustomerRedirect = () => {
    // Save current lead data and redirect to customer page
    navigate(tp('/customers'), {
      state: {
        fromLead: {
          name: formData.name,
          company: formData.company,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          customer_type: formData.customer_type,
        },
        returnToLead: true
      }
    });
  };

  const handleStateChange = (state) => {
    const stateData = ALL_STATES.find(s => s.state === state);
    setFormData({
      ...formData,
      state: state,
      region: stateData ? stateData.region : ''
    });
  };

  const addProduct = () => {
    setFormData({
      ...formData,
      products: [...formData.products, { _key: `p${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, product_id: '', product_name: '', quantity: 1 }]
    });
  };

  const updateProduct = (index, field, value) => {
    const newProducts = [...formData.products];
    newProducts[index][field] = value;
    
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        newProducts[index].product_name = product.name;
      }
    }
    
    setFormData({ ...formData, products: newProducts });
  };

  const removeProduct = (index) => {
    setFormData({
      ...formData,
      products: formData.products.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        lead_value: parseFloat(formData.lead_value)
      };
      
      if (editingLead) {
        await axios.put(`${API}/leads/${editingLead.id}`, submitData);
      } else {
        const response = await axios.post(`${API}/leads`, submitData);
        // If new customer (no customer_id), auto-create customer from lead
        if (!formData.customer_id && customerSource === 'new') {
          try {
            const custResp = await axios.post(`${API}/customers/from-lead?lead_id=${response.data.id}`);
            // Redirect to customer detail to fill remaining info
            navigate(`/customers/${custResp.data.id}`, {
              state: { fromNewLead: true, leadId: response.data.id }
            });
            return;
          } catch (err) {
            console.error('Error auto-creating customer:', err);
          }
        }
      }
      fetchLeads();
      fetchSummary();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving lead:', error);
      notify(error.response?.data?.detail || 'Error saving lead');
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete lead?',
      message: 'Are you sure you want to delete this lead? This cannot be undone.',
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/leads/${id}`);
      fetchLeads();
      fetchSummary();
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name,
      company: lead.company,
      email: lead.email,
      phone: lead.phone,
      address: lead.address || '',
      lead_value: lead.lead_value || '',
      products: lead.products || [],
      customer_type: lead.customer_type || 'Dealer',
      expected_closing_date: lead.expected_closing_date || '',
      state: lead.state || '',
      city: lead.city || '',
      region: lead.region || '',
      is_greenfield: !!lead.is_greenfield,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLead(null);
    setCustomerSource('existing');
    setFormData({
      name: '',
      company: '',
      email: '',
      phone: '',
      address: '',
      lead_value: '',
      products: [],
      customer_type: 'Dealer',
      expected_closing_date: '',
      state: '',
      city: '',
      region: '',
      customer_id: '',
      is_greenfield: false,
    });
  };

  const handleCreateQuote = (lead) => {
    // Navigate to create quotation with lead pre-selected
    navigate(tp('/quotations/create'), { state: { leadId: lead.id, leadName: lead.name } });
  };

  const filteredLeads = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let list = leads;
    if (q) {
      // Smart multi-field match: uploader can type name / company / email / phone /
      // city / state / region / assigned-person name / customer_code / gst / status.
      list = leads.filter(lead => {
        const hay = [
          lead.name, lead.company, lead.email, lead.phone,
          lead.city, lead.state, lead.region,
          lead.created_by_name, lead.assigned_to_name,
          lead.customer_code, lead.gst_number,
          lead.customer_type, lead.status,
        ].filter(Boolean).join(' | ').toLowerCase();
        return hay.includes(q);
      });
    }
    // Close-window filter — user picks "This Month" / "This Quarter (FY)" / etc.
    // "overdue" preset = expected_closing_date has passed AND status is still open.
    if (closeWindow === 'overdue') {
      list = list.filter(l => isForecastOverdue(l, 'expected_closing_date'));
    } else if (closeWindow && closeWindow !== 'all') {
      const win = computeCloseWindow(closeWindow);
      list = list.filter(l => withinCloseWindow(l, 'expected_closing_date', win));
    }
    // Standalone "Overdue forecast only" toggle — orthogonal to the window
    // preset (users can combine "This Month + only the ones already slipping").
    if (forecastOverdueOnly) {
      list = list.filter(l => isForecastOverdue(l, 'expected_closing_date'));
    }
    // Status filter — driven by the chip row OR by a KPI card click.
    if (statusFilter !== 'all') {
      list = list.filter(l => {
        const s = l.status;
        switch (statusFilter) {
          case 'open':
            return !['won', 'completed', 'lost'].includes(s);
          case 'quoted':
            return ['quoted', 'quote_approved', 'negotiation'].includes(s);
          case 'approved':
            return s === 'quote_approved';
          case 'won':
            return ['won', 'completed'].includes(s);
          case 'lost':
            return s === 'lost';
          default:
            return true;
        }
      });
    }
    // Sort — pipeline toolbar dropdown. Value key is lead_value.
    list = sortRows(list, sortBy, { valueKey: 'lead_value', dateKey: 'expected_closing_date' });
    // Sticky-pin promised leads at the top (Q4=b). Stable sort — within each
    // group we preserve the incoming order.
    if (Object.keys(promisedMap).length > 0) {
      const promised = [], other = [];
      for (const l of list) (promisedMap[l.id] ? promised : other).push(l);
      list = [...promised, ...other];
    }
    return list;
  }, [leads, searchTerm, promisedMap, closeWindow, sortBy, forecastOverdueOnly, statusFilter]);

  const visibleIds = useMemo(() => filteredLeads.map(l => l.id), [filteredLeads]);
  const selection = useBulkSelection(visibleIds);

  // Feb 2026 (iter78) — Row-level "Value" column reconciliation.
  // For Won/Completed leads the backend enriches each row with `booked_value_net`
  // (the same `_won_lead_value(net=True)` used by the WON REVENUE header tile).
  // For non-Won leads we still show the SE-entered pipeline `lead_value`.
  // This is what makes "sum of rows == header total" hold true.
  const displayLeadValue = (l) => {
    if ((l.status === 'won' || l.status === 'completed') &&
        typeof l.booked_value_net === 'number') {
      return l.booked_value_net;
    }
    return Number(l.lead_value || 0);
  };
  const leadValueLabel = (l) => (l.status === 'won' || l.status === 'completed')
    ? 'Won Value (net)'
    : 'Lead Value';
  const flagTooltip = (flags) => {
    if (!flags || !flags.length) return '';
    const msgs = flags.map((f) => {
      if (f === 'fractional_lead_value') return '⚠ Fractional lead value — looks like a paste/typo (paise beyond 2 decimals). Please review and correct.';
      if (f === 'booked_far_below_forecast') return '⚠ Booked value is far below the SE\'s original forecast — possible data-quality issue.';
      return `⚠ ${f}`;
    });
    return msgs.join('\n');
  };

  // Feb 2026 (iter84) — VP+ / President see a Gross Margin column so they
  // can eyeball profit alongside pipeline value. Backend `/api/leads` and
  // `/api/quotations` only enrich `gm_amount` / `gm_pct` for these roles,
  // so we mirror the RBAC on the frontend column definition.
  const canSeeGm = ['Vice President', 'President'].includes(user?.role);
  const { withTransport } = useGmTransport();
  const gmTone = (pct) => {
    if (pct == null) return 'text-[#6D8E87]';
    if (pct >= 30) return 'text-emerald-700';
    if (pct >= 15) return 'text-amber-700';
    return 'text-rose-700';
  };

  // Bulk state
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkReassignModal, setShowBulkReassignModal] = useState(false);
  const [salesExecsForBulk, setSalesExecsForBulk] = useState([]);
  const [bulkTarget, setBulkTarget] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkReassigning, setBulkReassigning] = useState(false);
  const [bulkCrossRegionInfo, setBulkCrossRegionInfo] = useState(null);

  const openBulkReassign = async () => {
    setBulkTarget(''); setBulkReason(''); setBulkCrossRegionInfo(null);
    setShowBulkReassignModal(true);
    if (salesExecsForBulk.length === 0) {
      try {
        const r = await axios.get(`${API}/users/sales-executives`);
        setSalesExecsForBulk(r.data.users || []);
      } catch (e) { console.error(e); }
    }
  };

  const submitBulkReassign = async (force = false) => {
    if (!bulkTarget || bulkReason.trim().length < 5) return;
    setBulkReassigning(true);
    try {
      const r = await axios.post(`${API}/leads/bulk-reassign`, {
        lead_ids: selection.selectedIds,
        new_assignee_id: bulkTarget,
        reason: bulkReason.trim(),
        force_cross_region: force,
      });
      if (r.data.requires_confirmation) {
        setBulkCrossRegionInfo(r.data);
        return;
      }
      notify(`✅ ${r.data.reassigned.length} lead(s) reassigned to ${r.data.target.name}.` +
        (r.data.already_assigned.length ? `\n${r.data.already_assigned.length} already assigned to this user (skipped).` : '') +
        (r.data.not_found.length ? `\n${r.data.not_found.length} lead(s) not found.` : ''));
      setShowBulkReassignModal(false);
      setBulkCrossRegionInfo(null);
      selection.clear();
      await fetchLeads();
      fetchSummary();
    } catch (e) {
      notify(e.response?.data?.detail || 'Bulk reassign failed');
    } finally { setBulkReassigning(false); }
  };

  const submitBulkDelete = async ({ reason }) => {
    setBulkDeleting(true);
    try {
      const r = await axios.post(`${API}/leads/bulk-delete`, {
        lead_ids: selection.selectedIds,
        reason,
      });
      notify(`✅ ${r.data.deleted} lead(s) deleted.`);
      setShowBulkDeleteDialog(false);
      selection.clear();
      await fetchLeads();
      fetchSummary();
    } catch (e) {
      notify(e.response?.data?.detail || 'Bulk delete failed');
    } finally { setBulkDeleting(false); }
  };

  if (loading) {
    return <PageSkeleton />;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 lg:mb-8">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="leads-title">
            Leads Management
          </h1>
          <p className="text-[#6D8E87]">Manage your customer leads</p>
          {canSeeGm && <GmTransportToggle className="mt-3" testid="leads-gm-transport-toggle" />}
        </div>
        <div className="flex gap-3">
          {canBulkUpload && (
            <button onClick={() => setShowBulkModal(true)}
              className="flex items-center gap-2 px-5 py-3 border-2 border-[#00a69c] text-[#00a69c] rounded-full font-medium hover:bg-[#E2F3F0] transition"
              data-testid="bulk-leads-button">
              <Upload size={20} weight="bold" /> Bulk Upload
            </button>
          )}
          {canAddLead && (
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="add-lead-button"
            >
              <Plus size={20} weight="bold" />
              Add Lead
            </button>
          )}
        </div>
      </div>

      {/* Manager-only: filter the whole Leads page by one or more owners
          (Sales Execs / RSMs / etc). Auto-hides for non-manager roles. */}
      <div className="flex items-center justify-between mb-2">
        <ViewToggle mode={viewMode} onChange={setViewMode} testIdPrefix="leads-view" />
        <OwnerFilter value={ownerFilter} onChange={setOwnerFilter} testid="leads-owner-filter" />
      </div>

      {/* Pipeline toolbar — close-window presets + sort + forecast-overdue + saved views. */}
      <PipelineToolbar
        pageKey="leads"
        window={closeWindow}
        onWindowChange={setCloseWindow}
        sortBy={sortBy}
        onSortChange={setSortBy}
        forecastOverdueOnly={forecastOverdueOnly}
        onForecastOverdueChange={setForecastOverdueOnly}
      />

      {/* Summary rollup — pipeline / quoted / approved / approval-rate. Every
          KPI card is a filter shortcut: click a card to filter the list
          below to that status; click again (or "All") to reset. */}
      <SummaryCards
        testid="leads-summary"
        loading={summaryLoading}
        items={summary ? [
          {
            label: 'Pipeline value', value: inr(summary.pipeline_value), icon: ChartLine,
            sub: `${summary.lead_count} lead${summary.lead_count === 1 ? '' : 's'}`,
            testid: 'summary-pipeline',
            onClick: () => setStatusFilter(statusFilter === 'open' ? 'all' : 'open'),
            active: statusFilter === 'open',
          },
          {
            label: 'Quoted', value: inr(summary.quoted_value), icon: FileTextIcon,
            sub: `${summary.quotation_count} quotation${summary.quotation_count === 1 ? '' : 's'}`,
            testid: 'summary-quoted',
            onClick: () => setStatusFilter(statusFilter === 'quoted' ? 'all' : 'quoted'),
            active: statusFilter === 'quoted',
          },
          {
            label: 'Approved (pipeline)', value: inr(summary.approved_value), icon: CheckCircle,
            sub: `${summary.approved_quotation_count} approved · not yet won`,
            testid: 'summary-approved',
            onClick: () => setStatusFilter(statusFilter === 'approved' ? 'all' : 'approved'),
            active: statusFilter === 'approved',
          },
          {
            label: 'Won revenue', value: inr(summary.won_revenue), icon: CurrencyInr,
            tone: 'revenue',
            sub: `${summary.won_count || 0} won deal${summary.won_count === 1 ? '' : 's'}`,
            testid: 'summary-won-revenue',
            onClick: () => setStatusFilter(statusFilter === 'won' ? 'all' : 'won'),
            active: statusFilter === 'won',
          },
          {
            label: 'Approval rate', value: `${summary.approval_rate_pct}%`, icon: CurrencyInr,
            tone: summary.approval_rate_pct >= 50 ? 'revenue' : 'warning',
            sub: 'approved / quoted',
            testid: 'summary-approval-rate',
            // No filter for the ratio — clicking cycles to the "approved" view
            // as the closest actionable slice.
            onClick: () => setStatusFilter(statusFilter === 'approved' ? 'all' : 'approved'),
            active: false,   // never highlighted since it's a shortcut, not a filter of its own
          },
        ] : []}
      />

      {/* Status filter chip row — companion to the KPI cards. Also gives
          the user an explicit "Lost" filter, which none of the cards surface. */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="leads-status-chips">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#6D8E87] mr-1">Status:</span>
        {[
          { key: 'all',      label: 'All' },
          { key: 'open',     label: 'Open' },
          { key: 'quoted',   label: 'Quoted' },
          { key: 'approved', label: 'Approved' },
          { key: 'won',      label: 'Won' },
          { key: 'lost',     label: 'Lost' },
        ].map(c => (
          <button
            key={c.key}
            onClick={() => setStatusFilter(c.key)}
            data-testid={`leads-status-${c.key}`}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition ${
              statusFilter === c.key
                ? 'bg-[#00a69c] text-white border-[#00a69c]'
                : 'bg-white text-[#3B5A54] border-[#D5E8E4] hover:border-[#00a69c]'
            }`}
          >
            {c.label}
          </button>
        ))}
        {statusFilter !== 'all' && (
          <span className="text-[11px] text-[#6D8E87] ml-1" data-testid="leads-status-filter-count">
            · showing {filteredLeads.length} of {leads.length}
          </span>
        )}
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#6D8E87]" />
          <input
            type="text"
            placeholder="Search by name, company, city, region, assignee, customer code, GST…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] focus:border-[#00bfb3] outline-none"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Bulk-select header (VP+ / NSM+) — only shows if there are leads visible */}
      {canBulkSelect && filteredLeads.length > 0 && (
        <div className="flex items-center gap-2 mb-3 text-sm text-[#3B5A54]" data-testid="bulk-select-header">
          <button
            type="button"
            onClick={selection.toggleAll}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-[#E2F3F0] transition"
            data-testid="bulk-select-all-toggle"
          >
            {selection.isAllSelected
              ? <CheckSquare size={20} weight="fill" className="text-[#00a69c]" />
              : <Square size={20} className={selection.isSomeSelected ? 'text-[#00a69c]' : 'text-[#6D8E87]'} />}
            <span className="font-medium">
              {selection.isAllSelected ? 'Deselect all' : `Select all ${filteredLeads.length}`}
            </span>
          </button>
          {selection.count > 0 && (
            <span className="text-xs text-[#6D8E87]">· {selection.count} selected</span>
          )}
        </div>
      )}

      {/* Leads Grid */}
      {viewMode === 'cards' && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-24" data-testid="leads-grid">
        {filteredLeads.map((lead) => (
          <div
            key={lead.id}
            className={`medical-card p-6 card-hover cursor-pointer relative ${selection.isSelected(lead.id) ? 'ring-2 ring-[#00a69c] shadow-lg' : ''} ${promisedMap[lead.id] ? 'border-2 border-amber-400' : ''}`}
            data-testid={`lead-card-${lead.id}`}
            onClick={() => navigate(`/leads/${lead.id}`)}
          >
            {/* Promised-closing tag — a corner ribbon that visually pins this
                lead as "I've committed this in the current review". Amber to
                stay visible without shouting. */}
            {promisedMap[lead.id] && (
              <div className="absolute -top-2 -right-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider shadow" data-testid={`promised-badge-${lead.id}`}>
                🎯 {promisedMap[lead.id]}
              </div>
            )}
            {canBulkSelect && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); selection.toggle(lead.id); }}
                className="absolute -top-2 -left-2 w-7 h-7 flex items-center justify-center bg-white border border-[#D5E8E4] rounded-full shadow hover:shadow-md transition z-10"
                data-testid={`lead-checkbox-${lead.id}`}
                aria-label="Select lead"
                style={{ position: 'relative', float: 'left', marginRight: 8, marginTop: -4 }}
              >
                {selection.isSelected(lead.id)
                  ? <CheckSquare size={20} weight="fill" className="text-[#00a69c]" />
                  : <Square size={20} className="text-[#6D8E87]" />}
              </button>
            )}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-semibold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
                    {lead.name}
                  </h3>
                  {lead.status && lead.status !== 'new' && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${
                      lead.status === 'won' ? 'bg-emerald-100 text-emerald-700' :
                      lead.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                      lead.status === 'lost' ? 'bg-red-100 text-red-700' :
                      lead.status === 'extended' ? 'bg-amber-100 text-amber-700' :
                      lead.status === 'active' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`} data-testid={`lead-status-${lead.id}`}>{lead.status}</span>
                  )}
                  {isForecastOverdue(lead, 'expected_closing_date') && (
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-rose-100 text-rose-700 border border-rose-300"
                      title={`Expected to close on ${lead.expected_closing_date} — that date has passed`}
                      data-testid={`lead-forecast-overdue-${lead.id}`}
                    >
                      Forecast Overdue
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#6D8E87]">{lead.company}</p>
                {lead.customer_type && (
                  <span className="inline-block mt-2 px-3 py-1 bg-[#E2F3F0] text-[#00a69c] text-xs rounded-full">
                    {lead.customer_type}
                  </span>
                )}
                {lead.customer_code && (
                  <span className="inline-block mt-2 ml-2 px-3 py-1 bg-[#FEF3C7] text-[#92400E] text-xs rounded-full font-semibold" data-testid={`lead-customer-code-${lead.id}`}>
                    {lead.customer_code}
                  </span>
                )}
                {lead.origin_tender_id && (
                  <span
                    className="inline-flex items-center gap-1 mt-2 ml-2 px-3 py-1 bg-[#EEF2FF] text-[#4338CA] text-xs rounded-full font-semibold cursor-pointer hover:bg-[#E0E7FF] transition"
                    onClick={(e) => { e.stopPropagation(); navigate(tp(`/tenders/${lead.origin_tender_id}`)); }}
                    title="Click to open source tender"
                    data-testid={`lead-origin-tender-${lead.id}`}
                  >
                    <Briefcase size={12} weight="fill" /> From Tender
                  </span>
                )}
              </div>
              <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                <button
                  onClick={() => handleEdit(lead)}
                  className="w-8 h-8 flex items-center justify-center text-[#00a69c] hover:bg-[#E2F3F0] rounded-lg transition"
                  data-testid={`edit-lead-${lead.id}`}
                >
                  <Pencil size={18} />
                </button>
                <button
                  onClick={() => handleDelete(lead.id)}
                  className="w-8 h-8 flex items-center justify-center text-[#EF4444] hover:bg-red-50 rounded-lg transition"
                  data-testid={`delete-lead-${lead.id}`}
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>
            
            <div className="space-y-2 text-sm mb-4">
              {(lead.lead_value || lead.booked_value_net) && (
                <div className="flex justify-between items-center">
                  <span className="text-[#6D8E87]">{leadValueLabel(lead)}:</span>
                  <span className="flex items-center gap-1.5">
                    {lead.value_flags && lead.value_flags.length > 0 && (
                      <Warning
                        size={16}
                        weight="fill"
                        className="text-amber-500"
                        data-testid={`lead-value-warning-${lead.id}`}
                        title={flagTooltip(lead.value_flags)}
                      />
                    )}
                    <span className="font-semibold text-[#00a69c]">₹{displayLeadValue(lead).toLocaleString('en-IN')}</span>
                  </span>
                </div>
              )}
              {canSeeGm && pickGmPct(lead, withTransport) != null && (
                <div className="flex justify-between items-center" data-testid={`lead-card-gm-${lead.id}`}>
                  <span className="text-[#6D8E87]">Gross Margin:</span>
                  <span
                    className={`font-semibold ${gmTone(pickGmPct(lead, withTransport))}`}
                    title={`GM ₹${Number(lead.gm_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}${lead.gm_complete === false ? ' · partial (some items missing COGS)' : ''}`}
                  >
                    {pickGmPct(lead, withTransport).toFixed(1)}%
                    {lead.gm_complete === false && <sup className="text-[9px] text-amber-600">*</sup>}
                  </span>
                </div>
              )}
              <p className="text-[#3B5A54]"><span className="font-medium">Email:</span> {lead.email}</p>
              <p className="text-[#3B5A54]"><span className="font-medium">Phone:</span> {lead.phone}</p>
              {lead.city && lead.state && (
                <p className="text-[#3B5A54]"><span className="font-medium">Location:</span> {lead.city}, {lead.state}</p>
              )}
              {lead.region && (
                <p className="text-[#3B5A54]"><span className="font-medium">Region:</span> {lead.region}</p>
              )}
              {lead.expected_closing_date && (
                <p className="text-[#3B5A54]">
                  <span className="font-medium">Expected Close:</span> {new Date(lead.expected_closing_date).toLocaleDateString()}
                </p>
              )}
            </div>

            <div className="flex gap-2" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => navigate(`/leads/${lead.id}`)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-[#00a69c] text-[#00a69c] rounded-lg hover:bg-[#E2F3F0] transition"
                data-testid={`view-lead-${lead.id}`}
              >
                <ArrowSquareOut size={18} />
                View
              </button>
              <button
                onClick={() => handleCreateQuote(lead)}
                disabled={!canCreateQuote}
                title={canCreateQuote ? '' : 'Tender Executives create quotes via the Tenders module'}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition ${canCreateQuote ? 'bg-[#00a69c] text-white hover:bg-[#00bfb3]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                data-testid={`create-quote-${lead.id}`}
              >
                <FileText size={18} />
                Quote
              </button>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Feb 2026 — List (Power Table) view — flip via ViewToggle above. */}
      {viewMode === 'list' && (
        <div className="pb-24">
          <PowerTable
            testIdPrefix="leads-table"
            initialSort={{ key: 'updated_at', dir: 'desc' }}
            selection={canBulkSelect ? selection : undefined}
            onRowClick={(row) => navigate(`/leads/${row.id}`)}
            rows={filteredLeads}
            columns={[
              { key: 'name', label: 'Lead', sortable: true, cell: (r) => (
                <div>
                  <div className="font-semibold text-[#052A23] flex items-center gap-1.5">
                    {r.name}
                    {r.origin_tender_id && (
                      <span
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-[#EEF2FF] text-[#4338CA] text-[9px] rounded font-bold cursor-pointer hover:bg-[#E0E7FF]"
                        onClick={(e) => { e.stopPropagation(); navigate(tp(`/tenders/${r.origin_tender_id}`)); }}
                        title="Click to open source tender"
                        data-testid={`lead-origin-tender-${r.id}`}
                      >
                        <Briefcase size={9} weight="fill" /> TENDER
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#6D8E87]">{r.company || '—'}</div>
                </div>
              ) },
              { key: 'city', label: 'City', sortable: true, cell: (r) => r.city || '—' },
              { key: 'owner_name', label: 'Owner', sortable: true, cell: (r) => r.owner_name || r.assigned_to_name || '—' },
              { key: 'lead_value', label: 'Value', sortable: true, className: 'text-right',
                accessor: (r) => displayLeadValue(r),
                cell: (r) => (
                  <span className="inline-flex items-center gap-1 justify-end">
                    {r.value_flags && r.value_flags.length > 0 && (
                      <Warning
                        size={14}
                        weight="fill"
                        className="text-amber-500"
                        data-testid={`lead-value-warning-${r.id}`}
                        title={flagTooltip(r.value_flags)}
                      />
                    )}
                    <span className="font-semibold text-[#00a69c]">₹{displayLeadValue(r).toLocaleString('en-IN')}</span>
                  </span>
                ) },
              ...(canSeeGm ? [{
                key: 'gm_pct', label: 'GM %', sortable: true, className: 'text-right',
                accessor: (r) => (pickGmPct(r, withTransport) == null ? -1 : Number(pickGmPct(r, withTransport))),
                cell: (r) => {
                  if (pickGmPct(r, withTransport) == null) {
                    return <span className="text-[11px] text-[#6D8E87]" title="No approved quote or COGS not captured">—</span>;
                  }
                  return (
                    <span
                      className={`inline-flex items-center gap-1 justify-end text-xs font-semibold ${gmTone(pickGmPct(r, withTransport))}`}
                      title={`GM ₹${Number(r.gm_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}${r.gm_complete === false ? ' · partial (some items missing COGS)' : ''}`}
                      data-testid={`lead-gm-${r.id}`}
                    >
                      {pickGmPct(r, withTransport).toFixed(1)}%
                      {r.gm_complete === false && <sup className="text-[9px] text-amber-600">*</sup>}
                    </span>
                  );
                },
              }] : []),
              { key: 'status', label: 'Status', sortable: true, cell: (r) => (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  r.status === 'won' ? 'bg-emerald-100 text-emerald-700'
                  : r.status === 'lost' ? 'bg-rose-100 text-rose-700'
                  : r.status === 'in_progress' ? 'bg-sky-100 text-sky-700'
                  : 'bg-amber-100 text-amber-700'
                }`}>{(r.status || '').replace(/_/g, ' ').toUpperCase()}</span>
              ) },
              { key: 'expected_closing_date', label: 'Close By', sortable: true,
                cell: (r) => r.expected_closing_date ? new Date(r.expected_closing_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—' },
              { key: 'updated_at', label: 'Age', sortable: true,
                accessor: (r) => r.created_at ? -new Date(r.created_at).getTime() : 0,
                cell: (r) => {
                  if (!r.created_at) return '—';
                  const days = Math.floor((Date.now() - new Date(r.created_at).getTime()) / 86400000);
                  return <span className="text-xs text-[#6D8E87]">{days}d</span>;
                } },
            ]}
          />
        </div>
      )}
      {showModal && (
        <Dialog open={showModal} onOpenChange={handleCloseModal}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="lead-modal">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                {editingLead ? 'Edit Lead' : 'Add New Lead'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Customer Selection */}
              {!editingLead && (
                <div className="p-4 bg-[#F4F9F7] rounded-lg border border-[#D5E8E4] mb-4" data-testid="customer-selection">
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Customer</label>
                  <div className="flex gap-3 mb-3">
                    <button type="button"
                      onClick={() => { setCustomerSource('existing'); setFormData(prev => ({ ...prev, customer_id: '' })); }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${customerSource === 'existing' ? 'bg-[#00a69c] text-white' : 'bg-white border border-[#D5E8E4] text-[#3B5A54]'}`}
                      data-testid="existing-customer-btn">
                      Existing Customer
                    </button>
                    <button type="button"
                      onClick={() => { setCustomerSource('new'); setFormData(prev => ({ ...prev, customer_id: '' })); }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${customerSource === 'new' ? 'bg-[#00a69c] text-white' : 'bg-white border border-[#D5E8E4] text-[#3B5A54]'}`}
                      data-testid="new-customer-btn">
                      New Customer
                    </button>
                  </div>
                  {customerSource === 'existing' ? (
                    <select
                      value={formData.customer_id}
                      onChange={(e) => handleCustomerSelect(e.target.value)}
                      className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                      data-testid="customer-dropdown"
                    >
                      <option value="">-- Select a customer --</option>
                      {customers.map(c => (
                        <option key={c.id} value={c.id}>{c.customer_code} - {c.organisation_name}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-xs text-[#6D8E87]">
                      Fill in the lead details below. After submission, you&apos;ll be redirected to complete the customer profile.
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    required
                    data-testid="lead-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Company *</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    required
                    data-testid="lead-company-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    required
                    data-testid="lead-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    required
                    data-testid="lead-phone-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Customer Type *</label>
                  <select
                    value={formData.customer_type}
                    onChange={(e) => setFormData({ ...formData, customer_type: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    required
                    data-testid="customer-type-select"
                  >
                    {CUSTOMER_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">
                    Lead Value (₹) *
                    <span className="ml-2 text-[10px] font-normal text-[#6D8E87]">Enter value <b>excluding GST</b></span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.lead_value}
                    onChange={(e) => setFormData({ ...formData, lead_value: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    required
                    data-testid="lead-value-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">State *</label>
                  <select
                    value={formData.state}
                    onChange={(e) => handleStateChange(e.target.value)}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    required
                    data-testid="state-select"
                  >
                    <option value="">Select State</option>
                    {ALL_STATES.map(({ state }) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">City *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    required
                    placeholder="Enter city"
                    data-testid="city-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Region (Auto-filled)</label>
                  <input
                    type="text"
                    value={formData.region}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg bg-[#F4F9F7] outline-none"
                    readOnly
                    data-testid="region-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Expected Closing Date *</label>
                  <input
                    type="date"
                    value={formData.expected_closing_date}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setFormData({ ...formData, expected_closing_date: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    required
                    data-testid="expected-date-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-2">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  rows="2"
                  data-testid="lead-address-input"
                />
              </div>

              {/* Slice 2 (Feb 2026) — Greenfield flag. Feeds the
                  `auto:greenfield_value` goal data source so managers can
                  track "prospect value from brand-new accounts" separately
                  from renewals / repeat business. */}
              <div className="p-3 bg-[#F4F9F7] rounded-lg border border-[#D5E8E4]">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData.is_greenfield}
                    onChange={(e) => setFormData({ ...formData, is_greenfield: e.target.checked })}
                    className="w-4 h-4 accent-[#00a69c]"
                    data-testid="lead-greenfield-checkbox"
                  />
                  <span className="text-sm font-medium text-[#3B5A54]">Greenfield prospect</span>
                </label>
                <p className="text-[11px] text-[#6D8E87] mt-1 ml-6">Tick if this is a first-time / brand-new account (not a renewal or repeat). Contributes to the Greenfield Value goal metric.</p>
              </div>

              {/* Products Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-[#3B5A54]">Products</label>
                  <button
                    type="button"
                    onClick={addProduct}
                    className="text-sm text-[#00a69c] hover:underline"
                    data-testid="add-product-button"
                  >
                    + Add Product
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.products.map((product, index) => (
                    <div key={product._key || `row-${index}`} className="flex gap-2" data-testid={`product-row-${index}`}>
                      <select
                        value={product.product_id}
                        onChange={(e) => updateProduct(index, 'product_id', e.target.value)}
                        className="flex-1 px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                      >
                        <option value="">Select Product</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>{p.name} - ₹{p.price}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="1"
                        value={product.quantity}
                        onChange={(e) => updateProduct(index, 'quantity', parseInt(e.target.value))}
                        className="w-24 px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                        placeholder="Qty"
                      />
                      <button
                        type="button"
                        onClick={() => removeProduct(index)}
                        className="w-10 h-10 flex items-center justify-center text-[#EF4444] hover:bg-red-50 rounded-lg transition"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-secondary flex-1"
                  data-testid="cancel-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1"
                  data-testid="save-lead-button"
                >
                  {editingLead ? 'Update' : 'Create'} Lead
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
      <BulkUploadModal open={showBulkModal} onClose={() => setShowBulkModal(false)}
        title="Bulk Upload Leads" exportUrl="/leads-export/csv" importUrl="/leads-import/bulk"
        templateName="leads_template.csv" onSuccess={fetchLeads}
        instructions="CSV with columns: name, company, email, phone, address, lead_value, customer_type, expected_closing_date, state, city, region. Name and company are required." />

      {/* Sticky action bar shown whenever leads are selected */}
      <BulkActionBar count={selection.count} onClear={selection.clear} testid="leads-bulk-action-bar">
        {canBulkReassign && (
          <button
            type="button"
            onClick={openBulkReassign}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-[#00a69c] hover:bg-[#008b83] rounded-full transition"
            data-testid="leads-bulk-reassign-btn"
          >
            <ArrowsLeftRight size={14} weight="bold" /> Reassign
          </button>
        )}
        {canBulkDelete && (
          <button
            type="button"
            onClick={() => setShowBulkDeleteDialog(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-red-600 hover:bg-red-700 rounded-full transition"
            data-testid="leads-bulk-delete-btn"
          >
            <Trash size={14} weight="bold" /> Delete
          </button>
        )}
      </BulkActionBar>

      <BulkDeleteDialog
        open={showBulkDeleteDialog}
        onClose={() => setShowBulkDeleteDialog(false)}
        onConfirm={submitBulkDelete}
        entityLabel="lead" entityLabelPlural="leads"
        count={selection.count}
        loading={bulkDeleting}
      />

      {/* Bulk Reassign Modal */}
      <Dialog open={showBulkReassignModal} onOpenChange={(v) => !v && !bulkReassigning && setShowBulkReassignModal(false)}>
        <DialogContent className="sm:max-w-[520px]" data-testid="bulk-reassign-modal">
          <DialogHeader>
            <DialogTitle>Reassign {selection.count} lead(s)</DialogTitle>
          </DialogHeader>

          {!bulkCrossRegionInfo && (
            <div className="space-y-4 mt-2">
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">New Assignee *</label>
                <select
                  value={bulkTarget}
                  onChange={(e) => setBulkTarget(e.target.value)}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg bg-white"
                  data-testid="bulk-reassign-target-select"
                >
                  <option value="">-- Select team member --</option>
                  {salesExecsForBulk.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name} — {u.role}{u.region ? ` (${u.region})` : ' (No region)'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">Reason (min 5 chars) *</label>
                <textarea
                  rows={2}
                  value={bulkReason}
                  onChange={(e) => setBulkReason(e.target.value)}
                  placeholder="e.g. Reassigning South territory to new hire"
                  className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm"
                  data-testid="bulk-reassign-reason-input"
                />
              </div>
            </div>
          )}

          {bulkCrossRegionInfo && (
            <div className="space-y-3 mt-2" data-testid="bulk-reassign-cross-region-confirm">
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
                <p className="font-semibold mb-1">Cross-region reassignment</p>
                <p>
                  <b>{bulkCrossRegionInfo.cross_region_count}</b> of the {selection.count} selected lead(s) belong to a
                  different region than <b>{bulkCrossRegionInfo.target_name}</b> ({bulkCrossRegionInfo.target_region}).
                </p>
                <p className="mt-2">Do you want to force-reassign all leads anyway?</p>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <button type="button" onClick={() => setShowBulkReassignModal(false)} disabled={bulkReassigning}
              className="px-4 py-2 text-sm text-[#3B5A54] border border-[#D5E8E4] rounded-lg hover:bg-[#F4F9F7]"
              data-testid="bulk-reassign-cancel">Cancel</button>
            {!bulkCrossRegionInfo ? (
              <button
                type="button"
                onClick={() => submitBulkReassign(false)}
                disabled={!bulkTarget || bulkReason.trim().length < 5 || bulkReassigning}
                className="px-4 py-2 text-sm font-semibold text-white bg-[#00a69c] hover:bg-[#008b83] rounded-lg disabled:bg-[#00a69c]/50"
                data-testid="bulk-reassign-submit"
              >
                {bulkReassigning ? 'Reassigning...' : 'Reassign'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => submitBulkReassign(true)}
                disabled={bulkReassigning}
                className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg"
                data-testid="bulk-reassign-force"
              >
                {bulkReassigning ? 'Reassigning...' : 'Force reassign all'}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
